import { useEffect, useMemo, useState } from 'react'
import { createScheduledRerun, getSchedulesForQuestion } from '../api/client'
import ForecastSettings, { type ForecastConfig } from './ForecastSettings'
import { useUIStore } from '../store/uiStore'
import { useSessionStore } from '../store/sessionStore'
import type { ForecastRun } from '../types'
import './RerunModal.css'

function configFromRun(run: ForecastRun): ForecastConfig {
  // Handle backwards compatibility: if old reasoning_effort exists, use it for both
  const legacyReasoning = run.config?.reasoning_effort as any
  return {
    formatter_model: run.config?.formatter_model || run.config?.models?.formatter || 'gpt-5-nano',
    predictor_model: run.config?.predictor_model || run.config?.models?.predictor || 'gpt-5-nano',
    supervisor_model: run.config?.supervisor_model || run.config?.models?.supervisor || 'gpt-5-mini',
    search_depth: (run.config?.search_depth as any) || 'low',
    predictor_reasoning_effort: (run.config?.predictor_reasoning_effort as any) || legacyReasoning || 'low',
    supervisor_reasoning_effort: (run.config?.supervisor_reasoning_effort as any) || legacyReasoning || 'medium',
    num_agents: run.config?.num_agents || 6,
    agent_configs: run.config?.agent_configs || undefined,
  }
}

// Convert local time to UTC for cron expression
function localTimeToUTC(hour: number, minute: number, timezoneOffset: number): { hour: number; minute: number } {
  const totalMinutes = hour * 60 + minute - timezoneOffset
  const utcHour = Math.floor((totalMinutes / 60 + 24) % 24)
  const utcMinute = ((totalMinutes % 60) + 60) % 60
  return { hour: utcHour, minute: utcMinute }
}

// Generate cron expression from user-friendly inputs
function buildCronExpression(
  scheduleType: string,
  hour: number,
  minute: number,
  timezoneOffset: number,
  dayOfWeek?: number,
  dayOfMonth?: number
): string {
  const utc = localTimeToUTC(hour, minute, timezoneOffset)

  if (scheduleType === 'daily') {
    return `${utc.minute} ${utc.hour} * * *`
  } else if (scheduleType === 'weekly') {
    return `${utc.minute} ${utc.hour} * * ${dayOfWeek ?? 1}`
  } else if (scheduleType === 'monthly') {
    return `${utc.minute} ${utc.hour} ${dayOfMonth ?? 1} * *`
  }
  return `${utc.minute} ${utc.hour} * * *`
}

export default function ScheduledRerunsModal({
  questionId,
  activeRun,
}: {
  questionId: string | null | undefined
  activeRun: ForecastRun | null
}) {
  const isOpen = useUIStore((s) => s.modals.scheduleReruns)
  const closeModal = useUIStore((s) => s.closeModal)
  const isAdmin = useSessionStore((s) => s.isAdmin)

  const baseConfig = useMemo(() => (activeRun ? configFromRun(activeRun) : null), [activeRun])
  const [scheduleType, setScheduleType] = useState<'daily' | 'weekly' | 'monthly' | 'custom'>('daily')
  const [hour, setHour] = useState(9)
  const [minute, setMinute] = useState(0)
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone)
  const [dayOfWeek, setDayOfWeek] = useState(1) // Monday
  const [dayOfMonth, setDayOfMonth] = useState(1)
  const [customDateTimes, setCustomDateTimes] = useState<string[]>([''])
  const [configMode, setConfigMode] = useState<'same' | 'custom'>('same')
  const [cfg, setCfg] = useState<ForecastConfig | null>(baseConfig)
  const [maxRunsInput, setMaxRunsInput] = useState('20')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [existingSchedulesCount, setExistingSchedulesCount] = useState(0)

  // Load existing schedules count
  useEffect(() => {
    if (isOpen && questionId) {
      getSchedulesForQuestion(questionId).then(schedules => {
        setExistingSchedulesCount(schedules.filter(s => s.is_active).length)
      }).catch(() => {
        setExistingSchedulesCount(0)
      })
    }
  }, [isOpen, questionId])

  if (!isOpen) return null

  const maxRuns = parseInt(maxRunsInput) || 20
  const scheduleLimit = isAdmin ? 20 : 5
  const remainingSchedules = Math.max(0, scheduleLimit - existingSchedulesCount)

  // Get timezone offset in minutes for selected timezone
  const getTimezoneOffset = () => {
    try {
      const now = new Date()
      const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }))
      const tzDate = new Date(now.toLocaleString('en-US', { timeZone: timezone }))
      return (tzDate.getTime() - utcDate.getTime()) / (1000 * 60)
    } catch {
      return new Date().getTimezoneOffset() * -1
    }
  }
  const timezoneOffset = getTimezoneOffset()

  const canSubmit = Boolean(
    questionId &&
    activeRun &&
    cfg &&
    maxRuns >= 1 &&
    maxRuns <= 100 &&
    remainingSchedules > 0 &&
    (scheduleType !== 'custom' || customDateTimes.some(dt => dt.trim()))
  )

  const handleClose = () => {
    closeModal('scheduleReruns')
    setScheduleType('daily')
    setHour(9)
    setMinute(0)
    setDayOfWeek(1)
    setDayOfMonth(1)
    setCustomDateTimes([''])
    setConfigMode('same')
    setCfg(baseConfig)
    setMaxRunsInput('20')
  }

  const addCustomDateTime = () => {
    if (customDateTimes.length < 20) {
      setCustomDateTimes([...customDateTimes, ''])
    }
  }

  const removeCustomDateTime = (index: number) => {
    if (customDateTimes.length > 1) {
      setCustomDateTimes(customDateTimes.filter((_, i) => i !== index))
    }
  }

  const updateCustomDateTime = (index: number, value: string) => {
    const updated = [...customDateTimes]
    updated[index] = value
    setCustomDateTimes(updated)
  }

  const handleSubmit = async () => {
    if (!canSubmit || !questionId || !cfg) return
    setIsSubmitting(true)
    try {
      if (scheduleType === 'custom') {
        // Create multiple one-time schedules for custom datetimes
        const validDateTimes = customDateTimes.filter(dt => dt.trim())

        if (validDateTimes.length === 0) {
          alert('Please add at least one valid date/time')
          setIsSubmitting(false)
          return
        }

        let successCount = 0
        for (const dateTimeStr of validDateTimes) {
          // Parse the datetime-local value and treat it as the selected timezone
          const dt = new Date(dateTimeStr)
          // Apply timezone offset to convert to UTC
          const localTime = new Date(dt.getTime() - timezoneOffset * 60 * 1000)
          const cronExpression = `${localTime.getUTCMinutes()} ${localTime.getUTCHours()} ${localTime.getUTCDate()} ${localTime.getUTCMonth() + 1} *`

          const request = {
            question_id: questionId,
            schedule_type: 'custom' as const,
            cron_expression: cronExpression,
            forecast_config: {
              ...cfg,
              agent_configs: cfg.agent_configs || undefined,
            },
            max_runs: 1, // One-time run for each custom datetime
          }

          try {
            await createScheduledRerun(request)
            successCount++
          } catch (e) {
            console.error(`Failed to create schedule for ${dateTimeStr}:`, e)
          }
        }

        if (successCount > 0) {
          alert(`Created ${successCount} scheduled run${successCount > 1 ? 's' : ''}!`)
          handleClose()
        } else {
          alert('Failed to create any schedules. Please try again.')
        }
      } else {
        // Regular recurring schedule
        const cronExpression = buildCronExpression(scheduleType, hour, minute, timezoneOffset, dayOfWeek, dayOfMonth)

        const request = {
          question_id: questionId,
          schedule_type: 'custom' as const,
          cron_expression: cronExpression,
          forecast_config: {
            ...cfg,
            agent_configs: cfg.agent_configs || undefined,
          },
          max_runs: maxRuns,
        }

        await createScheduledRerun(request)
        alert(`Schedule created! This forecast will rerun ${scheduleType} (max ${maxRuns} times)`)
        handleClose()
      }
    } catch (e) {
      console.error('Failed to create schedule:', e)
      alert(`Failed to create schedule: ${e instanceof Error ? e.message : 'Unknown error'}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content rerun-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Schedule reruns</h2>
          <button className="modal-close" onClick={handleClose} type="button">
            ×
          </button>
        </div>

        <div className="modal-body">
          {remainingSchedules === 0 && (
            <div style={{
              marginBottom: '16px',
              padding: '12px',
              background: 'rgba(168, 72, 72, 0.1)',
              border: '1px solid rgba(168, 72, 72, 0.3)',
              borderRadius: '8px',
              fontSize: '0.875em'
            }}>
              ⚠️ You've reached your limit of {scheduleLimit} active schedules. Please deactivate or delete an existing schedule to create a new one.
            </div>
          )}
          {remainingSchedules > 0 && remainingSchedules <= 2 && (
            <div style={{
              marginBottom: '16px',
              padding: '12px',
              background: 'rgba(242, 153, 74, 0.1)',
              border: '1px solid rgba(242, 153, 74, 0.3)',
              borderRadius: '8px',
              fontSize: '0.875em'
            }}>
              ℹ️ You have {remainingSchedules} of {scheduleLimit} schedule slots remaining.
            </div>
          )}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
              Schedule Type
            </label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                type="button"
                className={`rerun-mode ${scheduleType === 'daily' ? 'active' : ''}`}
                onClick={() => setScheduleType('daily')}
              >
                Daily
              </button>
              <button
                type="button"
                className={`rerun-mode ${scheduleType === 'weekly' ? 'active' : ''}`}
                onClick={() => setScheduleType('weekly')}
              >
                Weekly
              </button>
              <button
                type="button"
                className={`rerun-mode ${scheduleType === 'monthly' ? 'active' : ''}`}
                onClick={() => setScheduleType('monthly')}
              >
                Monthly
              </button>
              <button
                type="button"
                className={`rerun-mode ${scheduleType === 'custom' ? 'active' : ''}`}
                onClick={() => setScheduleType('custom')}
              >
                Custom
              </button>
            </div>

            {scheduleType !== 'custom' && (
              <div style={{ marginTop: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '0.9em' }}>
                  Time
                </label>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.75em', opacity: 0.7 }}>
                      Hour
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="23"
                      value={hour}
                      onChange={(e) => setHour(Math.max(0, Math.min(23, parseInt(e.target.value) || 0)))}
                      style={{
                        width: '60px',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                      }}
                    />
                  </div>
                  <span style={{ fontSize: '1.2em', paddingTop: '16px' }}>:</span>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.75em', opacity: 0.7 }}>
                      Minute
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={minute}
                      onChange={(e) => setMinute(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                      style={{
                        width: '60px',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginTop: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.75em', opacity: 0.7 }}>
                    Timezone
                  </label>
                  <select
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '0.875em'
                    }}
                  >
                    <optgroup label="Common">
                      <option value="UTC">UTC</option>
                      <option value="America/New_York">America/New_York (EST/EDT)</option>
                      <option value="America/Chicago">America/Chicago (CST/CDT)</option>
                      <option value="America/Denver">America/Denver (MST/MDT)</option>
                      <option value="America/Los_Angeles">America/Los_Angeles (PST/PDT)</option>
                      <option value="Europe/London">Europe/London (GMT/BST)</option>
                      <option value="Europe/Paris">Europe/Paris (CET/CEST)</option>
                      <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                      <option value="Asia/Shanghai">Asia/Shanghai (CST)</option>
                      <option value="Australia/Sydney">Australia/Sydney (AEST/AEDT)</option>
                    </optgroup>
                    <optgroup label="Americas">
                      <option value="America/Anchorage">America/Anchorage</option>
                      <option value="America/Phoenix">America/Phoenix</option>
                      <option value="America/Toronto">America/Toronto</option>
                      <option value="America/Vancouver">America/Vancouver</option>
                      <option value="America/Mexico_City">America/Mexico_City</option>
                      <option value="America/Sao_Paulo">America/Sao_Paulo</option>
                      <option value="America/Buenos_Aires">America/Buenos_Aires</option>
                    </optgroup>
                    <optgroup label="Europe/Africa">
                      <option value="Europe/Berlin">Europe/Berlin</option>
                      <option value="Europe/Madrid">Europe/Madrid</option>
                      <option value="Europe/Rome">Europe/Rome</option>
                      <option value="Europe/Moscow">Europe/Moscow</option>
                      <option value="Europe/Istanbul">Europe/Istanbul</option>
                      <option value="Africa/Cairo">Africa/Cairo</option>
                      <option value="Africa/Johannesburg">Africa/Johannesburg</option>
                    </optgroup>
                    <optgroup label="Asia/Pacific">
                      <option value="Asia/Dubai">Asia/Dubai</option>
                      <option value="Asia/Kolkata">Asia/Kolkata</option>
                      <option value="Asia/Singapore">Asia/Singapore</option>
                      <option value="Asia/Hong_Kong">Asia/Hong_Kong</option>
                      <option value="Asia/Seoul">Asia/Seoul</option>
                      <option value="Pacific/Auckland">Pacific/Auckland</option>
                    </optgroup>
                  </select>
                </div>

                {scheduleType === 'weekly' && (
                  <div style={{ marginTop: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.9em', fontWeight: 600 }}>
                      Day of Week
                    </label>
                    <select
                      value={dayOfWeek}
                      onChange={(e) => setDayOfWeek(parseInt(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                      }}
                    >
                      {dayNames.map((name, idx) => (
                        <option key={idx} value={idx}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {scheduleType === 'monthly' && (
                  <div style={{ marginTop: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.9em', fontWeight: 600 }}>
                      Day of Month
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={dayOfMonth}
                      onChange={(e) => setDayOfMonth(Math.max(1, Math.min(31, parseInt(e.target.value) || 1)))}
                      style={{
                        width: '80px',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {scheduleType === 'custom' && (
              <div style={{ marginTop: '12px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.875em', fontWeight: 600 }}>
                  Schedule specific run times
                </label>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.75em', opacity: 0.7 }}>
                    Timezone
                  </label>
                  <select
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '0.875em'
                    }}
                  >
                    <optgroup label="Common">
                      <option value="UTC">UTC</option>
                      <option value="America/New_York">America/New_York (EST/EDT)</option>
                      <option value="America/Chicago">America/Chicago (CST/CDT)</option>
                      <option value="America/Denver">America/Denver (MST/MDT)</option>
                      <option value="America/Los_Angeles">America/Los_Angeles (PST/PDT)</option>
                      <option value="Europe/London">Europe/London (GMT/BST)</option>
                      <option value="Europe/Paris">Europe/Paris (CET/CEST)</option>
                      <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                      <option value="Asia/Shanghai">Asia/Shanghai (CST)</option>
                      <option value="Australia/Sydney">Australia/Sydney (AEST/AEDT)</option>
                    </optgroup>
                    <optgroup label="Americas">
                      <option value="America/Anchorage">America/Anchorage</option>
                      <option value="America/Phoenix">America/Phoenix</option>
                      <option value="America/Toronto">America/Toronto</option>
                      <option value="America/Vancouver">America/Vancouver</option>
                      <option value="America/Mexico_City">America/Mexico_City</option>
                      <option value="America/Sao_Paulo">America/Sao_Paulo</option>
                      <option value="America/Buenos_Aires">America/Buenos_Aires</option>
                    </optgroup>
                    <optgroup label="Europe/Africa">
                      <option value="Europe/Berlin">Europe/Berlin</option>
                      <option value="Europe/Madrid">Europe/Madrid</option>
                      <option value="Europe/Rome">Europe/Rome</option>
                      <option value="Europe/Moscow">Europe/Moscow</option>
                      <option value="Europe/Istanbul">Europe/Istanbul</option>
                      <option value="Africa/Cairo">Africa/Cairo</option>
                      <option value="Africa/Johannesburg">Africa/Johannesburg</option>
                    </optgroup>
                    <optgroup label="Asia/Pacific">
                      <option value="Asia/Dubai">Asia/Dubai</option>
                      <option value="Asia/Kolkata">Asia/Kolkata</option>
                      <option value="Asia/Singapore">Asia/Singapore</option>
                      <option value="Asia/Hong_Kong">Asia/Hong_Kong</option>
                      <option value="Asia/Seoul">Asia/Seoul</option>
                      <option value="Pacific/Auckland">Pacific/Auckland</option>
                    </optgroup>
                  </select>
                </div>
                {customDateTimes.map((dateTime, index) => (
                  <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                    <input
                      type="datetime-local"
                      value={dateTime}
                      onChange={(e) => updateCustomDateTime(index, e.target.value)}
                      style={{
                        flex: 1,
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                      }}
                    />
                    {customDateTimes.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCustomDateTime(index)}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          background: '#fff',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                        }}
                        title="Remove this time"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                {customDateTimes.length < 20 && (
                  <button
                    type="button"
                    onClick={addCustomDateTime}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      background: '#fff',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      marginTop: '4px',
                    }}
                  >
                    + Add another time
                  </button>
                )}
                <div style={{ marginTop: '8px', fontSize: '0.75em', opacity: 0.7 }}>
                  {customDateTimes.length} / 20 scheduled times
                </div>
              </div>
            )}

            <div style={{ marginTop: '8px', fontSize: '0.875em', opacity: 0.7 }}>
              {scheduleType === 'daily' && `⏰ Runs every day at ${timeString} ${timezone.split('/').pop()?.replace(/_/g, ' ')}`}
              {scheduleType === 'weekly' && `⏰ Runs every ${dayNames[dayOfWeek]} at ${timeString} ${timezone.split('/').pop()?.replace(/_/g, ' ')}`}
              {scheduleType === 'monthly' && `⏰ Runs on day ${dayOfMonth} of each month at ${timeString} ${timezone.split('/').pop()?.replace(/_/g, ' ')}`}
              {scheduleType === 'custom' && customDateTimes.filter(dt => dt.trim()).length > 0 &&
                `⏰ ${customDateTimes.filter(dt => dt.trim()).length} one-time run${customDateTimes.filter(dt => dt.trim()).length > 1 ? 's' : ''} scheduled`}
            </div>
          </div>

          {scheduleType !== 'custom' && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                Max Runs (Safety Limit)
              </label>
              <input
                type="number"
                min="1"
                max="100"
                value={maxRunsInput}
                onChange={(e) => setMaxRunsInput(e.target.value)}
                onBlur={(e) => {
                  const val = parseInt(e.target.value)
                  if (isNaN(val) || val < 1) setMaxRunsInput('1')
                  else if (val > 100) setMaxRunsInput('100')
                }}
                style={{
                  width: '120px',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                }}
              />
              <div style={{ marginTop: '8px', fontSize: '0.875em', opacity: 0.7 }}>
                ⚠️ Schedule will auto-stop after {maxRuns} runs to prevent excessive costs
              </div>
            </div>
          )}

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
              Forecast Settings
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                className={`rerun-mode ${configMode === 'same' ? 'active' : ''}`}
                onClick={() => {
                  setConfigMode('same')
                  setCfg(baseConfig)
                }}
                disabled={!activeRun}
              >
                Same as original
              </button>
              <button
                type="button"
                className={`rerun-mode ${configMode === 'custom' ? 'active' : ''}`}
                onClick={() => {
                  setConfigMode('custom')
                  setCfg(baseConfig)
                }}
                disabled={!activeRun}
              >
                Custom settings
              </button>
            </div>
          </div>

          {configMode === 'custom' && cfg && (
            <div className="rerun-settings">
              <ForecastSettings config={cfg} onChange={setCfg} />
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            className="rounded-xl px-4 py-2.5 bg-green text-white font-semibold text-sm shadow-delphi hover:brightness-[0.98] disabled:opacity-60 disabled:cursor-not-allowed transition"
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
            type="button"
          >
            {isSubmitting ? 'Creating schedule…' : 'Create schedule'}
          </button>
          <button
            className="rounded-xl px-4 py-2.5 border border-rule bg-paper text-ink font-semibold text-sm shadow-delphi hover:bg-white/40 disabled:opacity-60 disabled:cursor-not-allowed transition"
            onClick={handleClose}
            disabled={isSubmitting}
            type="button"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
